import { Modal, Button } from 'antd';
import { useEffect } from 'react';
import { InputFromUser } from '../../../components/Input/input';
import { Options } from '../../../utils/Data';
import { DrRoundFormInit } from '../constants';
import { safeArray, safeObject } from '../utils/dataTransformers';
import moment from 'moment';
import { PrinterOutlined } from '@ant-design/icons';
import colors from '../../../utils/colors';
import { openPrintPreview } from '../../../components/Print';
import { message } from 'antd';

export const DoctorRoundModal = ({
    isModel,
    SetModel,
    autoDetectedRoundType,
    getAutoDetectedRoundType,
    formikProps,
    patientPlanList,
    patientDetail,
    handleSubmitSpecificRound,
}) => {
    const { values, errors, touched, handleBlur, setFieldValue, handleSubmit, isSubmitting, setSubmitting } = formikProps;
    const date = isModel?.date;
    const isPastDate = isModel?.isPastDate || false;
    const roundType = isModel?.roundType; // Get round type from button click

    // Initialize roundType in form data when modal opens
    useEffect(() => {
        if (date && roundType && isModel?.open) {
            // Check if this roundType already has data
            const existingData = values?.doctorRoundForm?.[date]?.[roundType];

            // If no data exists, initialize with roundType field set
            if (!existingData || !existingData.roundType) {
                setFieldValue(
                    'doctorRoundForm',
                    prev => {
                        // Create a deep copy to avoid mutation
                        const copy = JSON.parse(JSON.stringify(prev || {}));

                        if (!copy[date]) {
                            copy[date] = {};
                        }

                        if (!copy[date][roundType]) {
                            copy[date][roundType] = { ...DrRoundFormInit, roundType };
                        } else if (!copy[date][roundType].roundType) {
                            copy[date][roundType] = { ...copy[date][roundType], roundType };
                        }

                        return copy;
                    },
                    false // Don't validate on initialization
                );
            }
        }
    }, [date, roundType, isModel?.open, isPastDate]);

    // Get round type display info
    const getRoundTypeDisplay = (type) => {
        switch (type) {
            case 'morning':
                return { emoji: '🌅', label: 'Morning Round', color: '#faad14' };
            case 'evening':
                return { emoji: '🌆', label: 'Evening Round', color: '#722ed1' };
            case 'emergency':
                return { emoji: '🚨', label: 'Emergency Round', color: '#f5222d' };
            default:
                return { emoji: '🩺', label: 'Doctor Round', color: colors.role.doctor.primary };
        }
    };

    const roundDisplay = getRoundTypeDisplay(roundType);

    // Prepare patient data
    const customerData = patientPlanList?.customerId || {
        name: patientPlanList?.customerName,
        email: patientPlanList?.customerEmail,
        age: patientPlanList?.customerAge,
        phoneNo: patientPlanList?.customerPhoneNo,
        gender: patientPlanList?.customerGender,
        birthDate: patientPlanList?.customerBirthdate,
        registrationNo: patientPlanList?.registrationNo,
    };

    const displayData = patientDetail || customerData;

    const doctorRoundFormFill = (value, key) => {
        try {
            // Removed isPastDate check to enable past date editing
            /*
            if (isPastDate) {
                console.warn('Cannot modify data for past dates');
                return;
            }
            */

            if (!date || !roundType) {
                console.warn('No date or roundType selected for doctor round form');
                return;
            }

            setFieldValue(
                'doctorRoundForm',
                prev => {
                    // Create a deep copy to avoid mutation
                    const copy = JSON.parse(JSON.stringify(prev || {}));

                    // Initialize date object if it doesn't exist
                    if (!copy[date]) {
                        copy[date] = {};
                    }

                    // Initialize roundType object if it doesn't exist
                    if (!copy[date][roundType]) {
                        copy[date][roundType] = { ...DrRoundFormInit };
                    }

                    // Update the specific field for this date and roundType
                    copy[date][roundType] = {
                        ...copy[date][roundType],
                        [key]: value
                    };

                    return copy;
                },
                true
            );
        } catch (err) {
            console.error('Error filling doctor round form:', err);
        }
    };

    // Get data for the specific date and roundType
    const currentFormData = values?.doctorRoundForm?.[date]?.[roundType] || {};
    const currentErrors = errors?.doctorRoundForm?.[date]?.[roundType] || {};
    const currentTouched = touched?.doctorRoundForm?.[date]?.[roundType] || {};

    // Print function using Common Print Component
    const handlePrint = () => {
        const sections = [
            {
                title: 'Patient Information',
                fields: [
                    { label: 'Registration No', value: displayData?.registrationNo || '-' },
                    { label: 'Name', value: displayData?.name || '-' },
                    { label: 'Age', value: displayData?.age || '-' },
                    { label: 'Gender', value: displayData?.gender || '-' },
                ],
            },
            {
                title: 'Vital Signs',
                fields: [
                    { label: 'Round Type', value: currentFormData?.roundType || '-' },
                    { label: 'Temperature', value: `${currentFormData?.temperature || '-'} °F` },
                    { label: 'Pulse', value: `${currentFormData?.pulse || '-'} bpm` },
                    { label: 'SpO2', value: `${currentFormData?.spO2 || '-'} %` },
                    { label: 'Respiratory Rate', value: currentFormData?.respiratoryRate || '-' },
                ],
            },
            {
                title: 'Blood Pressure & Sugar',
                fields: [
                    { label: 'BP Morning', value: currentFormData?.BPMorning || '-' },
                    { label: 'BP Evening', value: currentFormData?.BPEvening || '-' },
                    { label: 'Fasting Sugar', value: currentFormData?.fastingSugarLevel || '-' },
                    { label: 'PP Sugar', value: currentFormData?.PPSugarLevel || '-' },
                    { label: 'Random Sugar', value: currentFormData?.randomSugarLevel || '-' },
                ],
            },
            {
                title: 'Ayurvedic Assessment',
                fields: [
                    { label: 'Nadi', value: formatArrayValue(currentFormData?.naad) },
                    { label: 'Yakrut Nada', value: formatArrayValue(currentFormData?.yakrutNada) },
                    { label: 'Pliha Nada', value: formatArrayValue(currentFormData?.plihaNada) },
                    { label: 'Mala', value: formatArrayValue(currentFormData?.Mala) },
                    { label: 'Mutra', value: formatArrayValue(currentFormData?.Mutra) },
                    { label: 'Jivha', value: formatArrayValue(currentFormData?.jivha) },
                ],
            },
            {
                title: 'Sparsha Sahatva (Palpation)',
                fields: safeArray(currentFormData?.sparshaSahatva || DrRoundFormInit.sparshaSahatva).map(item => {
                    const key = Object.keys(safeObject(item))?.[0];
                    return {
                        label: key,
                        value: formatArrayValue(item[key]),
                    };
                }),
            },
            {
                title: 'Clinical Notes',
                fields: [
                    { label: 'Notes', value: currentFormData?.note || '-' },
                ],
            }
        ];

        openPrintPreview(
            currentFormData,
            `Doctor Round Report - ${date ? moment(date).format('DD MMM YYYY') : ''}`,
            { sections }
        );
    };

    // Helper function to format array values for display
    const formatArrayValue = (value) => {
        if (!value) return '-';
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '-';
        }
        return value || '-';
    };

    // Section Header Component
    const SectionHeader = ({ icon, title, color = colors.role.doctor.primary }) => (
        <div style={{
            fontSize: '15px',
            fontWeight: '600',
            color: color,
            marginBottom: '16px',
            marginTop: '24px',
            paddingBottom: '8px',
            borderBottom: `2px solid ${color}`,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        }}>
            <span style={{ fontSize: '18px' }}>{icon}</span>
            {title}
        </div>
    );

    return (
        <Modal
            open={isModel?.open}
            closable
            forceRender
            zIndex={1040}
            width={1000}
            bodyStyle={{ maxHeight: '75vh', overflow: 'auto', padding: '24px' }}
            maskStyle={{ backdropFilter: 'blur(8px)' }}
            title={
                <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: isPastDate ? colors.category.config.primaryLight : roundDisplay.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    {roundDisplay.emoji} {roundDisplay.label} - {date ? moment(date).format('DD MMM YYYY') : ''}
                    {isPastDate && <span style={{ fontSize: '12px', color: colors.status.warning, fontWeight: '500' }}>(Past Date Editing Enabled)</span>}
                </div>
            }
            footer={
                <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        {isPastDate ? '⚠️ Historical record - Changes will update past data' : '💾 Click "Save Round Data" to save changes'}
                    </div>
                    <div>
                        <Button
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                            style={{ marginRight: '8px' }}
                        >
                            Print
                        </Button>
                        <Button
                            type="default"
                            onClick={() => SetModel({ date: null, open: false, isPastDate: false })}
                            style={{ marginRight: '8px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={isSubmitting}
                            disabled={isSubmitting}
                            onClick={async () => {
                                    setSubmitting(true);
                                    try {
                                        // Call handleSubmitSpecificRound with current round type
                                        await handleSubmitSpecificRound(values, date, roundType);
                                        message.success(`${roundDisplay.label} data saved successfully!`);
                                        // Close modal after successful save
                                        setTimeout(() => {
                                            SetModel({ date: null, open: false, isPastDate: false });
                                        }, 500);
                                    } catch (error) {
                                        console.error('Error saving doctor round data:', error);
                                        message.error('Failed to save doctor round data');
                                    } finally {
                                        setSubmitting(false);
                                    }
                                }}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Round Data'}
                            </Button>
                    </div>
                </div>
            }
            onCancel={() => SetModel({ date: null, open: false, isPastDate: false })}
        >
            <div className="row">
                {/* Patient Info - Compact Header */}
                {displayData && (
                    <div className="col-12 mb-3">
                        <div style={{
                            background: isPastDate ? colors.ui.background : colors.role.doctor.background,
                            border: `1px solid ${isPastDate ? colors.ui.border : colors.role.doctor.primaryLight}`,
                            borderRadius: '6px',
                            padding: '12px 16px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            fontSize: '13px',
                        }}>
                            {displayData.registrationNo && (
                                <div>
                                    <strong style={{ color: colors.ui.textSubtle }}>Reg:</strong>{' '}
                                    <span style={{ color: colors.ui.textMain, fontWeight: '500' }}>{displayData.registrationNo}</span>
                                </div>
                            )}
                            {displayData.name && (
                                <div>
                                    <strong style={{ color: colors.ui.textSubtle }}>Name:</strong>{' '}
                                    <span style={{ color: colors.ui.textMain, fontWeight: '500' }}>{displayData.name}</span>
                                </div>
                            )}
                            {displayData.age && (
                                <div>
                                    <strong style={{ color: colors.ui.textSubtle }}>Age:</strong>{' '}
                                    <span style={{ color: colors.ui.textMain, fontWeight: '500' }}>{displayData.age}</span>
                                </div>
                            )}
                            {displayData.gender && (
                                <div>
                                    <strong style={{ color: colors.ui.textSubtle }}>Gender:</strong>{' '}
                                    <span style={{ color: colors.ui.textMain, fontWeight: '500' }}>{displayData.gender}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Round Type */}
                <div className="col-12">
                    <InputFromUser
                        placeholder="Select Round Type"
                        label="Round Type"
                        type="singleSelect"
                        name="roundType"
                        FormControlClassName="form-control"
                        FormGroupClassName="col-12"
                        LabelClassName="col-form-label"
                        onChange={v => doctorRoundFormFill(v, 'roundType')}
                        onBlur={handleBlur}
                        value={currentFormData?.roundType || roundType}
                        options={[
                            { label: '🌅 Morning Round', value: 'morning' },
                            { label: '🌆 Evening Round', value: 'evening' },
                            { label: '🚨 Emergency Round', value: 'emergency' }
                        ]}
                        error={currentErrors.roundType}
                        touched={currentTouched.roundType}
                        disabled={false}
                    />
                </div>

                {/* SECTION 1: VITAL SIGNS */}
                <div className="col-12">
                    <SectionHeader title="Vital Signs" color={colors.category.clinical.primary} />
                </div>

                <InputFromUser
                    label="Temperature (°F)"
                    placeholder="e.g. 98.6"
                    type="number"
                    name="temperature"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-3 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'temperature')}
                    onBlur={handleBlur}
                    value={currentFormData?.temperature}
                    error={currentErrors.temperature}
                    touched={currentTouched.temperature}
                    disabled={false}
                />

                <InputFromUser
                    label="Pulse (bpm)"
                    placeholder="e.g. 72"
                    type="number"
                    name="pulse"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-3 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'pulse')}
                    onBlur={handleBlur}
                    value={currentFormData?.pulse}
                    error={currentErrors.pulse}
                    touched={currentTouched.pulse}
                    disabled={false}
                />

                <InputFromUser
                    label="SpO2 (%)"
                    placeholder="e.g. 98"
                    type="number"
                    name="spO2"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-3 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'spO2')}
                    onBlur={handleBlur}
                    value={currentFormData?.spO2}
                    error={currentErrors.spO2}
                    touched={currentTouched.spO2}
                    disabled={false}
                />

                <InputFromUser
                    label="Respiratory Rate"
                    placeholder="e.g. 16"
                    type="number"
                    name="respiratoryRate"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-3 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'respiratoryRate')}
                    onBlur={handleBlur}
                    value={currentFormData?.respiratoryRate}
                    error={currentErrors.respiratoryRate}
                    touched={currentTouched.respiratoryRate}
                    disabled={false}
                />

                {/* SECTION 2: BLOOD PRESSURE */}
                <div className="col-12">
                    <SectionHeader title="Blood Pressure" color={colors.status.warning} />
                </div>
                {["morning", "emergency"].includes(roundType) &&
                    <InputFromUser
                        label="BP Morning"
                        placeholder="e.g. 120/80"
                        type="text"
                        name="BPMorning"
                        FormControlClassName="form-control"
                        FormGroupClassName="col-md-6 col-sm-6"
                        LabelClassName="col-form-label"
                        onChange={e => doctorRoundFormFill(e?.target?.value, 'BPMorning')}
                        onBlur={handleBlur}
                        value={currentFormData?.BPMorning}
                        error={currentErrors.BPMorning}
                        touched={currentTouched.BPMorning}
                        disabled={false}
                    />}

                {["evening", "emergency"].includes(roundType) && <InputFromUser
                    label="BP Evening"
                    placeholder="e.g. 120/80"
                    type="text"
                    name="BPEvening"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-6 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'BPEvening')}
                    onBlur={handleBlur}
                    value={currentFormData?.BPEvening}
                    error={currentErrors.BPEvening}
                    touched={currentTouched.BPEvening}
                    disabled={false}
                />}

                {/* SECTION 3: BLOOD SUGAR LEVELS */}
                <div className="col-12">
                    <SectionHeader title="Blood Sugar Levels" color={colors.status.danger} />
                </div>

                <InputFromUser
                    label="Fasting Sugar"
                    placeholder="e.g. 90"
                    type="number"
                    name="fastingSugarLevel"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'fastingSugarLevel')}
                    onBlur={handleBlur}
                    value={currentFormData?.fastingSugarLevel}
                    error={currentErrors.fastingSugarLevel}
                    touched={currentTouched.fastingSugarLevel}
                    disabled={false}
                />

                <InputFromUser
                    label="PP Sugar"
                    placeholder="e.g. 140"
                    type="number"
                    name="PPSugarLevel"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'PPSugarLevel')}
                    onBlur={handleBlur}
                    value={currentFormData?.PPSugarLevel}
                    error={currentErrors.PPSugarLevel}
                    touched={currentTouched.PPSugarLevel}
                    disabled={false}
                />

                <InputFromUser
                    label="Random Sugar"
                    placeholder="e.g. 110"
                    type="number"
                    name="randomSugarLevel"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={e => doctorRoundFormFill(e?.target?.value, 'randomSugarLevel')}
                    onBlur={handleBlur}
                    value={currentFormData?.randomSugarLevel}
                    error={currentErrors.randomSugarLevel}
                    touched={currentTouched.randomSugarLevel}
                    disabled={false}
                />

                {/* SECTION 4: AYURVEDIC ASSESSMENT */}
                <div className="col-12">
                    <SectionHeader title="Ayurvedic Assessment" color={colors.category.scheduling.primary} />
                </div>

                {/* Nadi */}
                <InputFromUser
                    label="Nadi"
                    placeholder="Select Nadi"
                    type="multiple"
                    name="naad"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'naad')}
                    onBlur={handleBlur}
                    value={currentFormData?.naad}
                    options={safeArray(Options?.Nadi)}
                    error={currentErrors.naad}
                    touched={currentTouched.naad}
                    disabled={false}
                />

                <InputFromUser
                    label="Yakrut Nada"
                    type="multiple"
                    name="yakrutNada"
                    placeholder="Select Yakrut Nada"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'yakrutNada')}
                    onBlur={handleBlur}
                    value={currentFormData?.yakrutNada}
                    options={safeArray(Options?.Nada?.Yakrut)}
                    error={currentErrors.yakrutNada}
                    touched={currentTouched.yakrutNada}
                    disabled={false}
                />

                <InputFromUser
                    label="Pliha Nada"
                    type="multiple"
                    name="plihaNada"
                    placeholder="Select Pliha Nada"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'plihaNada')}
                    onBlur={handleBlur}
                    value={currentFormData?.plihaNada}
                    options={safeArray(Options?.PlihaNada)}
                    error={currentErrors.plihaNada}
                    touched={currentTouched.plihaNada}
                    disabled={false}
                />

                {/* Mala, Mutra, Jivha */}
                <InputFromUser
                    label="Mala"
                    type="multiple"
                    name="Mala"
                    placeholder="Select Mala"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'Mala')}
                    onBlur={handleBlur}
                    value={currentFormData?.Mala}
                    options={safeArray(Options?.Mala)}
                    error={currentErrors.Mala}
                    touched={currentTouched.Mala}
                    disabled={false}
                />

                <InputFromUser
                    label="Mutra"
                    type="multiple"
                    name="Mutra"
                    placeholder="Select Mutra"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'Mutra')}
                    onBlur={handleBlur}
                    value={currentFormData?.Mutra}
                    options={safeArray(Options?.Mutra)}
                    error={currentErrors.Mutra}
                    touched={currentTouched.Mutra}
                    disabled={false}
                />

                <InputFromUser
                    label="Jivha"
                    type="multiple"
                    name="jivha"
                    placeholder="Select Jivha"
                    FormControlClassName="form-control"
                    FormGroupClassName="col-md-4 col-sm-6"
                    LabelClassName="col-form-label"
                    onChange={v => doctorRoundFormFill(v, 'jivha')}
                    onBlur={handleBlur}
                    value={currentFormData?.jivha}
                    options={safeArray(Options?.Jivha)}
                    error={currentErrors.jivha}
                    touched={currentTouched.jivha}
                    disabled={false}
                />

                {/* SECTION 5: SPARSHA SAHATVA */}
                <div className="col-12">
                    <SectionHeader title="Sparsha Sahatva (Palpation)" color={colors.category.financial.primary} />
                </div>

                {safeArray(currentFormData?.sparshaSahatva || DrRoundFormInit.sparshaSahatva).map(
                    (item, i) => {
                        const NameV = Object.keys(safeObject(item))?.[0];
                        return (
                            <InputFromUser
                                type="multiple"
                                key={i + 'drRound'}
                                label={NameV}
                                FormControlClassName="form-control"
                                FormGroupClassName="col-md-6 col-sm-6"
                                LabelClassName="col-form-label"
                                name="sparshaSahatva"
                                onChange={value => {
                                    const newArr = safeArray(
                                        currentFormData?.sparshaSahatva || DrRoundFormInit.sparshaSahatva
                                    ).slice();
                                    newArr[i] = { [NameV]: value };
                                    doctorRoundFormFill(newArr, 'sparshaSahatva');
                                }}
                                onBlur={handleBlur}
                                value={item?.[NameV]}
                                options={
                                    NameV === 'Yakrut'
                                        ? safeArray(Options?.SparshasahatvaSubOptions?.Yakrut)
                                        : NameV === 'Pleeha'
                                            ? safeArray(Options?.SparshasahatvaSubOptions?.Pleeha)
                                            : NameV === 'Dakshi Vruka'
                                                ? safeArray(Options?.SparshasahatvaSubOptions?.DakshiVruka)
                                                : NameV === 'Vaam Vruka'
                                                    ? safeArray(Options?.SparshasahatvaSubOptions?.VaamVruka)
                                                    : NameV === 'Apan Kaksha'
                                                        ? safeArray(Options?.SparshasahatvaSubOptions?.ApanKaksha)
                                                        : []
                                }
                                touched={true}
                                placeholder={`Select ${NameV}`}
                                disabled={false}
                            />
                        );
                    }
                )}

                {/* SECTION 6: NOTES */}
                <div className="col-12">
                    <SectionHeader title="Clinical Notes" color={colors.ui.textMain} />
                </div>

                <div className="col-12">
                    <label className="col-form-label">Notes / Observations</label>
                    <textarea
                        className="form-control"
                        rows={10}
                        placeholder="Enter round observations, patient status, or special instructions..."
                        value={currentFormData?.note || ''}
                        onChange={e => doctorRoundFormFill(e?.target?.value, 'note')}
                        disabled={false}
                        style={{
                            borderRadius: '6px',
                            border: `1px solid ${colors.ui.border}`,
                            padding: '10px',
                            width: '100%',
                            marginBottom: '20px'
                        }}
                    />
                </div>

            </div>
        </Modal>
    );
};
